import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	createExportCNRefXls,
	createExportRawXls,
	ErrorResponse,
	ExportCNRefXlsResult,
	ExportRawXlsResult,
	findSkMaxPrice,
	SkMaxPriceResult,
	Update,
	updateSkMaxPrice as apiUpdateSkMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/SkMaxPrice';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';

const updateQueue = new PromiseQueue(1, Infinity);

export function fetchSkMaxPrice(groupId: string): ThunkAction<Promise<SkMaxPriceResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findSkMaxPrice, userId, groupId));
		dispatch({ type: ActionType.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionType.receive,
				groupId,
				request,
				skMaxPrice: response?.skMaxPrice ?? null,
			})
		);
	};
}

export function updateSkMaxPrice(groupId: string, update: Update): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		const request = updateQueue.add(() => dispatch(immediatelyUpdateSkMaxPrice(groupId, update)));
		dispatch({ type: ActionType.requestUpdate, groupId, request });
		return request;
	};
}

function immediatelyUpdateSkMaxPrice(groupId: string, update: Update): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(invokeApi(apiUpdateSkMaxPrice, userId, groupId, update))
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				type: ActionType.receiveUpdate,
				groupId,
				request,
				skMaxPrice: response?.skMaxPrice ?? null,
			})
		);
	};
}

export function handleSkMaxPriceUpdateResponse(response: any, groupId: string): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}

		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId)).catch((response) => handleError(response));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchSkMaxPrice(groupId)).catch((response) => handleError(response));
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchSkMaxPrice(groupId)).catch((response) => handleError(response));
		}
		dispatch(handleError(response));
	};
}

export function createSkMaxPriceExportCNRefXls(groupId: string): ThunkAction<Promise<ExportCNRefXlsResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(createExportCNRefXls, userId, groupId));
		dispatch({ type: ActionType.requestXlsCreate, groupId, request });

		return resolve(request, (response, error) =>
			dispatch({
				type: ActionType.receiveXlsCreate,
				request,
				validationResult: error
					? error instanceof ErrorResponse
						? new ValidationResult(groupId, false, [], [])
						: null
					: null,
			})
		);
	};
}

export function createSkMaxPriceExportRawXls(groupId: string): ThunkAction<Promise<ExportRawXlsResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(createExportRawXls, userId, groupId));
		dispatch({ type: ActionType.requestXlsCreate, groupId, request });

		return resolve(request, (response, error) =>
			dispatch({
				type: ActionType.receiveXlsCreate,
				request,
				validationResult: error
					? error instanceof ErrorResponse
						? new ValidationResult(groupId, false, [], [])
						: null
					: null,
			})
		);
	};
}

export function pruneSkMaxPrice(groupId: string): Action {
	return { type: ActionType.prune, groupId };
}

export function scheduleSkMaxPriceUpdate(groupId: string, update: Update): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		return new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateSkMaxPrice(groupId, update)));
			}, 2000);

			dispatch({ type: ActionType.scheduleUpdate, timeoutId, groupId, update });
		});
	};
}

export function commitSkMaxPriceUpdate(groupId: string): ThunkAction<Promise<void>, Action> {
	return async (dispatch, getState) => {
		const { update } = getState().skMaxPrice;
		if (!update || update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, data } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateSkMaxPrice(groupId, data));
		}
	};
}

export function handleCommitError(error: any): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
}
